import React, { useState } from "react";
import { safeJSONParse } from "../../utils/helper";
import { MessageInputHandle } from "../ChatWindow/MessageInput";
import MarkdownRenderer from "../MarkdownRenderer";
import AnswerComposerAgent from "./AnswerComposerAgent";
import ContractReviewerAgent from "./ContractReviewerAgent";
import CVScreenerAgent from "./CVScreenerAgent";
import DataTalkIntentClassifierAgent from "./DataTalkIntentClassifierAgent";
import DataTalkQueryPlannerAgent from "./DataTalkQueryPlannerAgent";
import DataTalkSqlGeneratorAgent from "./DataTalkSqlGeneratorAgent";
import EmailDraftingAgent from "./EmailDraftingAgent";
import HrAgentV2HitlAgent from "./HrAgentV2HitlAgent";
import InferenceWorkflowEndAgent from "./InferenceWorkflowEndAgent";
import InternalCommunicationAgent from "./InternalCommunicationAgent";
import JDComposerAgent from "./JDComposerAgent";
import JDDetailsAgent from "./JDDetailsAgent";
import LegalReviewerAgent from "./LegalReviewerAgent";
import LocalizerAgent from "./LocalizerAgent";
import MediaBriefAgent from "./MediaBriefAgent";
import NoCandidatesFoundAgent from "./NoCandidatesFoundAgent";
import OrchestratorAgent from "./OrchestratorAgent";
import PressReleaseAgent from "./PressReleaseAgent";
import PressReleaseQAAgent from "./PressReleaseQAAgent";
import PrMbWorkflowEndAgent from "./PrMbWorkflowEndAgent";
import RedrafterOrchestratorAgent from "./RedrafterOrchestratorAgent";
import ResumeFetcherAgent from "./ResumeFetcherAgent";
import RfqBuilderAgent from "./RfqBuilderAgent";
import RfqOrchestratorAgent from "./RfqOrchestratorAgent";
import ScoringAgent from "./ScoringAgent";
import SocialMediaInsightsAgent from "./SocialMediaInsightsAgent";
import SocialMediaIntakeAgent from "./SocialMediaIntakeAgent";
import SowAutoFillerAgent from "./SowAutoFillerAgent";
import SyntheticResumeAgent from "./SyntheticResumeAgent";
import TemplateDocumentSelectorAgent from "./TemplateDocumentSelectorAgent";
import TemplateGuidedIntakeAgent from "./TemplateGuidedIntakeAgent";
import TemplateReviewerAgent from "./TemplateReviewerAgent";
import TemplateReviewerChecklistAgent from "./TemplateReviewerChecklistAgent";
import TemplateReviewerIntakeAgent from "./TemplateReviewerIntakeAgent";
import TranslationAgent from "./TranslationAgent";
import UtilityAgent from "./UtilityAgent";
import ValidationCheckAgent from "./ValidationCheckAgent";
import WorkflowEndAgent from "./WorkflowEndAgent";

type AgentResponseProps = {
  agentId: string | undefined;
  content: string;
  inputRef?: React.RefObject<MessageInputHandle>;
  currentChat?: any[];
  messageId?: string;
};

/** HR-style JD panel — shown expanded by default with full markdown rendering */
function JDReviewPanel({
  title,
  jdId,
  jdText,
  message,
  isCustomized,
  nextStepHint,
}: {
  title: string;
  jdId: string;
  jdText: string;
  message: string;
  isCustomized?: boolean;
  nextStepHint: string;
}) {
  const [collapsed, setCollapsed] = useState(false);

  const accentColor = isCustomized ? "#7c3aed" : "#0e7490";
  const accentBg = isCustomized ? "#f5f3ff" : "#ecfeff";
  const accentBorder = isCustomized ? "#c4b5fd" : "#a5f3fc";
  const badgeLabel = isCustomized ? "Updated" : "Matched";
  const badgeBg = isCustomized ? "#ede9fe" : "#cffafe";
  const badgeColor = isCustomized ? "#6d28d9" : "#0e7490";

  return (
    <div
      style={{
        borderRadius: "12px",
        border: `1px solid ${accentBorder}`,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      {/* ── Header bar ── */}
      <div
        style={{
          padding: "14px 16px",
          background: `linear-gradient(135deg, ${accentBg}, #ffffff)`,
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            backgroundColor: badgeBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            flexShrink: 0,
          }}
        >
          {isCustomized ? "✏️" : "📋"}
        </div>

        {/* Title + badge */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: "15px", color: "#1e293b" }}>
              {title || `JD ${jdId}`}
            </span>
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: "100px",
                backgroundColor: badgeBg,
                color: badgeColor,
                letterSpacing: "0.02em",
              }}
            >
              {badgeLabel}
            </span>
          </div>
          {message && (
            <div style={{ fontSize: "13px", color: "#64748b", marginTop: "2px" }}>
              {message}
            </div>
          )}
        </div>

        {/* Collapse / Expand toggle */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          style={{
            background: "none",
            border: `1px solid ${accentBorder}`,
            borderRadius: "8px",
            padding: "4px 12px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
            color: accentColor,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            whiteSpace: "nowrap",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = accentBg)}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {collapsed ? "Show JD ▾" : "Collapse ▴"}
        </button>
      </div>

      {/* ── JD body (markdown) ── */}
      {!collapsed && (
        <div
          style={{
            borderTop: `1px solid ${accentBorder}`,
            backgroundColor: "#ffffff",
          }}
        >
          {/* Thin accent stripe */}
          <div style={{ height: "3px", background: `linear-gradient(90deg, ${accentColor}, ${accentBorder})` }} />

          <div
            style={{
              padding: "20px 24px",
              fontSize: "14px",
              lineHeight: "1.75",
              color: "#334155",
            }}
          >
            <MarkdownRenderer content={jdText} />
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "12px 24px",
              borderTop: "1px solid #f1f5f9",
              backgroundColor: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {/* Left: JD ID + status */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "12px", color: "#94a3b8" }}>
              {jdId && (
                <span style={{ fontFamily: "monospace", backgroundColor: "#f1f5f9", padding: "2px 8px", borderRadius: "4px" }}>
                  {jdId}
                </span>
              )}
              <span>
                {isCustomized ? "✏️ Customized" : "📄 Original"}
              </span>
            </div>

            {/* Right: hint */}
            <div style={{ fontSize: "12px", color: "#94a3b8", display: "flex", alignItems: "center", gap: "5px" }}>
              <span>💡</span>
              <span>{nextStepHint}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getJDNextStepHint(eventType: string, message: string, isCustomized: boolean): string {
  const lowerMessage = (message || "").toLowerCase();
  const cvReady = eventType === "awaiting_jd_approval" || lowerMessage.includes("cv is ready");

  if (cvReady) {
    return isCustomized
      ? "Type below to request more edits or approve to proceed"
      : "Type below to edit requirements or approve to proceed";
  }

  return isCustomized
    ? "Type below to request more edits or upload a CV to start screening"
    : "Type below to edit requirements or upload a CV to start screening";
}

export default function AgentResponse({
  agentId,
  content,
  inputRef,
  currentChat = [],
  messageId,
}: AgentResponseProps): JSX.Element | null {
  const parsedContent = safeJSONParse(content);

  // Handler for suggestion click - shared across multiple agents
  const handleSuggestionClick = (suggestion: string) => {
    if (inputRef?.current) {
      inputRef.current.sendPrompt(suggestion);
    }
  };

  // Early return for null/undefined/empty cases — render nothing instead of a dev warning
  const isEmpty = !parsedContent || (typeof parsedContent === 'object' && Object.keys(parsedContent).length === 0);

  if (!agentId && isEmpty) {
    return null;
  }

  // CV Screener HITL - check content structure for JD selection
  if (parsedContent?.answer?.type === "jd_selection" ||
      parsedContent?.answer?.data?.type === "jd_selection" ||
      parsedContent?.type === "jd_selection" ||
      (parsedContent?.answer?.matches && Array.isArray(parsedContent.answer.matches))) {
    console.log("🎯 Detected JD Selection - routing to HITL");
    return <HrAgentV2HitlAgent parsedContent={parsedContent} />;
  }

  // Handle error messages from workflow failures
  if (agentId === "error") {
    // Extract error from answer wrapper or top level
    const errorMessage = parsedContent?.answer?.error || parsedContent?.error || "Something went wrong. Please try again.";

    return (
      <div style={{ padding: "16px", backgroundColor: "#fffbeb", border: "1px solid #fbbf24", borderRadius: "8px" }}>
        <div style={{ color: "#92400e", fontWeight: 600, marginBottom: "8px", fontSize: "15px" }}>
          Heads Up
        </div>
        <div
          style={{
            color: "#78350f",
            fontSize: "13px",
            lineHeight: "1.6",
          }}
        >
          {errorMessage}
        </div>
      </div>
    );
  }

  if (agentId === "utility_agent") {
    return <UtilityAgent parsedContent={parsedContent} />;
  }

  if (agentId === "press_release_agent") {
    return (
      <PressReleaseAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (
    agentId === "press_release_qa_agent" ||
    agentId === "PressReleaseQAAgent"
  ) {
    return <PressReleaseQAAgent parsedContent={parsedContent} />;
  }

  if (agentId === "LocalizerAgent" || agentId === "localizer_agent") {
    return (
      <LocalizerAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (agentId == "orchestrator_new_agent") {
    return (
      <OrchestratorAgent
        parsedContent={parsedContent}
      />
    )
  }

  if (agentId === "media_brief_agent") {
    return (
      <MediaBriefAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (agentId === "social_media_insights_agent") {
    return <SocialMediaInsightsAgent parsedContent={parsedContent} />;
  }

  if (agentId === "internal_communication_agent") {
    return (
      <InternalCommunicationAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (agentId === "workflow_end") {
    return <WorkflowEndAgent parsedContent={parsedContent} currentChat={currentChat} />;
  }

  if (agentId === "pr_mb_workflow_end") {
    return <PrMbWorkflowEndAgent parsedContent={parsedContent} currentChat={currentChat} />;
  }

  if (agentId === "inference_workflow_end") {
    return <InferenceWorkflowEndAgent parsedContent={parsedContent} />;
  }

  if (
    agentId === "synthetic_resume_agent" ||
    agentId === "skill_inference_agent" ||
    agentId === "modify_requirements"
  ) {
    return <SyntheticResumeAgent parsedContent={parsedContent} agentId={agentId} />;
  }

  if (agentId == "jd_details_agent") {
    return <JDDetailsAgent parsedContent={parsedContent} />
  }

  if (agentId == "intent_classifier_agent") {
    return <DataTalkIntentClassifierAgent parsedContent={parsedContent} />
  }

  if (agentId == "query_planner_agent") {
    return <DataTalkQueryPlannerAgent parsedContent={parsedContent} />
  }

  if (agentId == "sql_generator_agent") {
    return <DataTalkSqlGeneratorAgent parsedContent={parsedContent} />
  }

  if (agentId === "jd_composer_agent") {
    return <JDComposerAgent parsedContent={parsedContent} />
  }
  if (agentId === "resume_fetcher_agent") {
    return <ResumeFetcherAgent parsedContent={parsedContent} />;
  }

  if (agentId === "scoring_agent" || agentId === "scoring_agent_new") {
    return <ScoringAgent parsedContent={parsedContent} agentId={agentId} />;
  }

  if (agentId === "email_drafting") {
    return <EmailDraftingAgent parsedContent={parsedContent} />;
  }

  if (agentId === "validation_check") {
    return <ValidationCheckAgent parsedContent={parsedContent} />;
  }

  if (agentId === "hr_agent_v2_hitl" || agentId === "role_validation_failed" || agentId === "cv_screener_hitl") {
    return <HrAgentV2HitlAgent parsedContent={parsedContent} />;
  }

  if (agentId === "no_candidates_found") {
    return <NoCandidatesFoundAgent parsedContent={parsedContent} />;
  }

  if (agentId === "translation_agent") {
    return (
      <TranslationAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (
    agentId === "template_reviewer_intake_agent" ||
    agentId === "template_reviewer_qa_agent"
  ) {
    return <TemplateReviewerIntakeAgent parsedContent={parsedContent} />;
  }

  if (
    agentId === "template_guided_intake_agent"
  ) {
    return <TemplateGuidedIntakeAgent parsedContent={parsedContent} />;
  }

  if (agentId === "template_reviewer_agent") {
    return <TemplateReviewerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "template_reviewer_checklist_agent") {
    return <TemplateReviewerChecklistAgent parsedContent={parsedContent} messageId={messageId || ""} />;
  }

  if (agentId === "template_document_selector_agent") {
    return <TemplateDocumentSelectorAgent parsedContent={parsedContent} />;
  }

  if (
    agentId === "contract_reviewer_orchestrator" ||
    agentId === "contract_reviewer_qa_agent" ||
    agentId === "ConsolidatorAgent" ||
    agentId === "consolidator_agent" ||
    agentId === "redrafting_agent"
  ) {
    return <ContractReviewerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "legal_reviewer_orchestrator") {
    return <LegalReviewerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "redrafter_orchestrator") {
    return <RedrafterOrchestratorAgent parsedContent={parsedContent} />;
  }

  if (agentId === "socialmedia_intake_agent") {
    return <SocialMediaIntakeAgent parsedContent={parsedContent} />;
  }

  if (agentId === "sow_auto_filler") {
    return <SowAutoFillerAgent parsedContent={parsedContent} messageId={messageId} />;
  }

  if (agentId === "answer_composer_agent") {
    return <AnswerComposerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "rfq_orchestrator" || agentId === "criteria_parser") {
    return <RfqOrchestratorAgent parsedContent={parsedContent} />;
  }

  if (agentId === "rfq_builder") {
    return <RfqBuilderAgent parsedContent={parsedContent} />;
  }

  // CV Screener Agents
  if (
    agentId === "jd_matcher_agent" ||
    agentId === "jd_customizer_agent" ||
    agentId === "router_agent" ||
    agentId === "intake_agent" ||
    agentId === "explainer_agent" ||
    agentId === "scorer_agent" ||
    agentId === "error"
  ) {
    // Extract the actual data - HR workflow wraps in 'answer' field
    const actualContent = parsedContent?.answer || parsedContent;

    // Check if this is the final scoring result (has agent_name and dimensions)
    // OR if it's an error response from scorer_agent
    if (
      (agentId === "scorer_agent" && actualContent?.agent_name && actualContent?.d1_jd_alignment) ||
      (agentId === "scorer_agent" && actualContent?.error) ||
      (agentId === "error" && actualContent?.error)
    ) {
      return <CVScreenerAgent parsedContent={actualContent} />;
    }

    // Explainer agent - render follow-up Q&A answers
    if (agentId === "explainer_agent") {
      const explanation = actualContent?.explanation || actualContent?.message || actualContent?.payload?.explanation || "";
      const queryType = actualContent?.query_type || actualContent?.payload?.query_type || "general";

      if (explanation) {
        return (
          <div style={{ padding: "12px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#166534", marginBottom: "8px" }}>
              {queryType === "why" ? "Why Score Explanation" : queryType === "what_if" ? "Improvement Analysis" : "Agent Details"}
            </div>
            <div style={{ fontSize: "13px", color: "#14532d", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
              {explanation}
            </div>
          </div>
        );
      }
    }

    // For intermediate status messages
    const message = actualContent?.message || actualContent?.payload?.message || "";
    const title = actualContent?.title || actualContent?.payload?.title || "";
    const eventType = actualContent?.event_type || actualContent?.payload?.event_type || "";
    const jdId = actualContent?.jd_id || actualContent?.updated_jd_id || "";
    const jdText = actualContent?.jd_text || actualContent?.payload?.jd_text || "";

    console.log("🎨 Rendering CV Screener status:", {
      agentId,
      eventType,
      message,
      title,
      jdId,
      jdText: jdText ? `${jdText.substring(0, 80)}...` : "",
      actualContent
    });

    // JD confirmed, customized, awaiting CV, or awaiting JD approval — show JD review panel
    if ((eventType === "jd_confirmed" || eventType === "jd_customized" || eventType === "awaiting_cv" || eventType === "awaiting_jd_approval") && jdText) {
      const nextStepHint = getJDNextStepHint(eventType, message, eventType === "jd_customized");
      return (
        <JDReviewPanel
          title={title}
          jdId={jdId}
          jdText={jdText}
          message={message}
          isCustomized={eventType === "jd_customized"}
          nextStepHint={nextStepHint}
        />
      );
    }

    return (
      <div style={{ padding: "12px", backgroundColor: "#f0f9ff", borderRadius: "8px", border: "1px solid #bae6fd" }}>
        {(eventType === "jd_confirmed" || eventType === "jd_customized") && (title || jdId) && (
          <div style={{ color: "#0369a1", fontSize: "14px", fontWeight: 500, marginBottom: "8px" }}>
            {eventType === "jd_customized" ? "✓ Job Description Updated:" : "✓ Job Description Matched:"} <strong>{title || `JD ${jdId}`}</strong>
          </div>
        )}
        {message && (
          <div style={{ color: "#0c4a6e", fontSize: "13px" }}>
            {message}
          </div>
        )}
        {!message && !eventType && (
          <div style={{ color: "#0c4a6e", fontSize: "13px" }}>
            Processing...
          </div>
        )}
      </div>
    );
  }

  // Fallback - try to render something useful
  console.error("No handler found for agent:", agentId);
  console.error("Parsed content:", parsedContent);

  // If answer has type and matches (JD selection), render HITL
  if (parsedContent?.answer && (parsedContent.answer.type === "jd_selection" || parsedContent.answer.matches)) {
    console.log("🎯 Late-caught JD Selection - routing to HITL");
    return <HrAgentV2HitlAgent parsedContent={parsedContent.answer} />;
  }

  // If there's a formatted_message anywhere in the structure, show it
  const formattedMessage =
    parsedContent?.answer?.formatted_message ||
    parsedContent?.formatted_message ||
    parsedContent?.answer?.data?.formatted_message;

  if (formattedMessage) {
    console.log("🎨 Rendering formatted_message fallback");
    return (
      <div style={{ padding: "16px", backgroundColor: "#f0f9ff", borderRadius: "8px", border: "1px solid #bae6fd" }}>
        <div dangerouslySetInnerHTML={{ __html: formattedMessage.replace(/\n/g, '<br/>') }} />
      </div>
    );
  }

  // Last resort - show debug info
  return (
    <div style={{ padding: "16px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px" }}>
      <div style={{ color: "#991b1b", fontWeight: 600, marginBottom: "8px" }}>
        Debug Info - No handler for this agent
      </div>
      <div style={{ fontSize: "12px", color: "#7f1d1d", fontFamily: "monospace" }}>
        <div><strong>Agent ID:</strong> {agentId || "undefined"}</div>
        <div><strong>Content Type:</strong> {typeof parsedContent}</div>
        <div style={{ marginTop: "8px", maxHeight: "200px", overflow: "auto", backgroundColor: "white", padding: "8px", borderRadius: "4px" }}>
          <pre>{JSON.stringify(parsedContent, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
}
